import { Action, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { LoadingState } from '@shared/types/loading-state';
import { defaultPaginationState, PaginationState } from '@shared/models/pagination-state';
import { Lead } from '@shared/models/lead';
import { LeadFilters } from '@shared/models/lead-filters';
import { loadLeads, loadLeadsFailure, loadLeadsSuccess } from '@features/leads/actions/load-leads.actions';
import { loadLead, loadLeadFailure, loadLeadSuccess } from '@features/leads/actions/load-lead.actions';
import { createLead, createLeadFailure, createLeadSuccess } from '@features/leads/actions/create-lead.actions';
import { updateLead, updateLeadFailure, updateLeadSuccess } from '@features/leads/actions/update-lead.actions';
import { setLeadFilters } from '@features/leads/actions/set-lead-filters.actions';
import { updateCustomerSuccess } from '@features/customers/admin/actions/update-customer.actions';

export const featureKey = 'leads';

export const leadsAdapter = createEntityAdapter<Lead>({
  selectId: (lead: Lead) => lead.attributes.id,
});

export interface State extends EntityState<Lead> {
  loadingState: LoadingState;
  paginationState: PaginationState;
  loadingText?: string;
  filters?: LeadFilters;
}

export const initialState: State = leadsAdapter.getInitialState({
  loadingState: 'not_loading' as LoadingState,
  paginationState: defaultPaginationState,
});

export const leadsReducer = createReducer(
  initialState,
  on(loadLeads, (state, { filters }) => ({
    ...state,
    loadingState: 'loading' as LoadingState,
    loadingText: undefined,
    filters: filters,
  })),
  on(loadLead, (state) => ({
    ...state,
    loadingState: 'loading' as LoadingState,
    loadingText: 'Loading lead details...',
  })),
  on(createLead, (state) => ({
    ...state,
    loadingState: 'loading' as LoadingState,
    loadingText: 'Creating lead...',
  })),
  on(updateLead, (state) => ({
    ...state,
    loadingState: 'loading' as LoadingState,
    loadingText: 'Updating lead...',
  })),
  on(loadLeadsSuccess, (state, { leads, paginationState }): State => {
    return leadsAdapter.setAll(leads, {
      ...state,
      loadingState: 'loaded' as LoadingState,
      paginationState,
    });
  }),
  on(updateCustomerSuccess, (state, { group }): State => {
    const leadToUpdate = Object.values(state.entities).find((lead) => lead?.attributes.group_id === group.id);
    if (!leadToUpdate) {
      return state;
    }
    const updatedLead: Lead = {
      ...leadToUpdate,
      attributes: {
        ...leadToUpdate.attributes,
        group: group,
      },
    };
    return leadsAdapter.upsertOne(updatedLead, {
      ...state,
      loadingState: 'loaded' as LoadingState,
      loadingText: undefined,
    });
  }),
  on(loadLeadSuccess, updateLeadSuccess, (state, { lead }): State => {
    return leadsAdapter.upsertOne(lead, {
      ...state,
      loadingState: 'loaded' as LoadingState,
      loadingText: undefined,
    });
  }),
  on(createLeadSuccess, (state, { lead }): State => {
    const newState = leadsAdapter.upsertOne(lead, {
      ...state,
      loadingState: 'loaded' as LoadingState,
      loadingText: undefined,
    });

    const ids = [lead.attributes.id, ...newState.ids.filter((id) => id !== lead.attributes.id)] as number[];
    return { ...newState, ids };
  }),
  on(setLeadFilters, (state, { filters }) => ({
    ...state,
    filters,
  })),
  on(loadLeadsFailure, loadLeadFailure, createLeadFailure, updateLeadFailure, (state) => ({
    ...state,
    loadingState: 'not_loading' as LoadingState,
    loadingText: undefined,
  })),
);

export function reducer(state: State | undefined, action: Action) {
  return leadsReducer(state, action);
}

export const leadsFeatureSelector = createFeatureSelector<State>(featureKey);

const { selectAll, selectEntities } = leadsAdapter.getSelectors(leadsFeatureSelector);

export const selectAllLeads = selectAll;

export const selectLeadsLoadingState = createSelector(leadsFeatureSelector, (state) => state.loadingState);

export const selectLeadsLoadingText = createSelector(leadsFeatureSelector, (state: State) => {
  return state.loadingText;
});

export const selectLeadsPaginationState = createSelector(leadsFeatureSelector, (state) => state.paginationState);

export const selectLeadFilters = createSelector(leadsFeatureSelector, (state) => state.filters);

export const selectLeadById = (leadId: number) =>
  createSelector(selectEntities, (entities) => (leadId == undefined ? undefined : entities[leadId]));
